import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import FillWidthCell from '../../../app/components/FillWidthCell';
import StyledTableRow from '../../../app/components/StyledTableRow';
import { useAppDispatch } from '../../../app/store/configureStore';
import { useTranslation } from 'react-i18next';
import LoadingComponent from '../../../app/layout/LoadingComponent';
import { fetchKitchenMealItemsAsync } from './kitchenMealItemsSlice';
import MealItem from '../../../app/data/models/mealItem';
import EmptyScreen from '../../../app/components/EmptyScreen';

export default function KitchenMealItems() {
  const { t } = useTranslation();
  const { restaurantId } = useParams<{
    restaurantId: string;
  }>();
  const { kitchenId } = useParams<{ kitchenId: string }>();
  const dispatch = useAppDispatch();
  const [kitchenMealItems, setKitchenMealItems] = useState<MealItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchKitchenMealItems = async () => {
      const result = await dispatch(
        fetchKitchenMealItemsAsync({ restaurantId, kitchenId })
      );
      if (result.meta.requestStatus === 'fulfilled') {
        setKitchenMealItems(result.payload as MealItem[]);
      } else {
        setKitchenMealItems([]);
      }
      setIsLoading(false);
    };
    fetchKitchenMealItems();
  }, [kitchenId, restaurantId, dispatch]);

  if (isLoading) return <LoadingComponent />;

  if (!kitchenMealItems || kitchenMealItems.length === 0)
    return <EmptyScreen emptyText={t('kitchen.details.no-meal-items')} />;

  return (
    <Box>
      <TableContainer
        component={Paper}
        sx={{
          width: '100%',
          overflowX: 'hidden',
          px: 2,
          mb: 2,
          '& tr:last-child td': { borderBottom: 'none' },
        }}
      >
        <Table
          sx={{
            width: '100%',
            tableLayout: 'fixed',
            '& td, & th': {
              boxSizing: 'border-box',
            },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 32, p: 1 }}>#</TableCell>
              <FillWidthCell align="left" sx={{ p: 1 }}>
                {t('kitchen.details.header.name')}
              </FillWidthCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {kitchenMealItems.map((mealItem, index) => (
              <StyledTableRow key={mealItem.id}>
                <TableCell sx={{ width: 32, px: 1, py: 2 }} align="left">
                  {index + 1}
                </TableCell>
                <FillWidthCell
                  align="left"
                  sx={{
                    px: 1,
                    py: 2,
                    whiteSpace: 'normal',
                    wordWrap: 'break-word',
                  }}
                >
                  {mealItem.name}
                </FillWidthCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
